import { useEffect, useState } from 'react'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Button, Select, SkeletonStack } from '@/shared/ui'
import type { ChangeEvent, ClientEventsFilter, ContactLabel } from '@/shared/api/types'
import { CONTACT_LABELS } from '@/entities/contact'
import { useCampaigns } from '@/entities/campaign'
import { useClientEvents } from '@/entities/client-event'
import {
  ClientEventRow,
  mergeClientEvents,
  useMarkEventsReadOnView,
} from '@/entities/client-event/ui'
import styles from './ClientEventsPage.module.css'

const PAGE_SIZE = 30
const ALL_LABELS = '__all__'
const EMPTY_EVENTS: ChangeEvent[] = []

type LabelFilter = ContactLabel | typeof ALL_LABELS

interface LoadedPage {
  cursor: number | null
  items: ChangeEvent[]
}

export function ClientEventsPage() {
  const [important, setImportant] = useState(false)
  const [campaignId, setCampaignId] = useState('')
  const [label, setLabel] = useState<LabelFilter>(ALL_LABELS)
  const campaigns = useCampaigns()

  const filter: ClientEventsFilter = {
    important: important || undefined,
    campaignId: campaignId ? Number(campaignId) : undefined,
    label: label === ALL_LABELS ? undefined : label,
  }
  const feedKey = `${important ? 'important' : 'all'}:${campaignId || 'all'}:${label}`
  const hasFilters = important || campaignId !== '' || label !== ALL_LABELS
  const campaignOptions = [
    {
      value: '',
      label: campaigns.isPending
        ? 'Загружаем обзвоны…'
        : campaigns.isError
          ? 'Все обзвоны (список недоступен)'
          : 'Все обзвоны',
    },
    ...(campaigns.data ?? []).map((campaign) => ({
      value: String(campaign.id),
      label: campaign.name.trim() || `Обзвон #${campaign.id}`,
    })),
  ]
  const labelOptions = [
    { value: ALL_LABELS, label: 'Любая метка' },
    ...CONTACT_LABELS.map((item) => ({ value: item.value, label: item.label })),
  ]

  return (
    <>
      <PageHeader
        title="Что нового по клиентам"
        description="Новые факты, договорённости и изменения карточек после разговоров агента."
      />

      <section className={styles.panel} aria-label="Лента изменений клиентов">
        <div className={styles.toolbar}>
          <button
            type="button"
            className={`${styles.importantToggle} ${important ? styles.importantActive : ''}`}
            aria-pressed={important}
            onClick={() => setImportant((value) => !value)}
          >
            <StarIcon />
            Только важное
          </button>

          <label className={styles.filter}>
            <span className={styles.filterLabel}>Обзвон</span>
            <Select
              className={styles.select}
              value={campaignId}
              options={campaignOptions}
              disabled={campaigns.isPending || campaigns.isError}
              onChange={(event) => setCampaignId(event.target.value)}
            />
          </label>

          <label className={styles.filter}>
            <span className={styles.filterLabel}>Метка</span>
            <Select
              className={styles.select}
              value={label}
              options={labelOptions}
              onChange={(event) => setLabel(event.target.value as LabelFilter)}
            />
          </label>

          {hasFilters && (
            <button
              type="button"
              className={styles.reset}
              onClick={() => {
                setImportant(false)
                setCampaignId('')
                setLabel(ALL_LABELS)
              }}
            >
              Сбросить
            </button>
          )}
        </div>

        {campaigns.isError && (
          <p className={styles.filterWarning} role="status">
            Список обзвонов не загрузился, но общая лента доступна.
          </p>
        )}

        <EventsFeed key={feedKey} filter={filter} filtered={hasFilters} />
      </section>
    </>
  )
}

function EventsFeed({
  filter,
  filtered,
}: {
  filter: ClientEventsFilter
  filtered: boolean
}) {
  const [cursor, setCursor] = useState<number | undefined>()
  const [pages, setPages] = useState<LoadedPage[]>([])
  const events = useClientEvents({ ...filter, limit: PAGE_SIZE, beforeId: cursor })
  const pageCursor = cursor ?? null

  useEffect(() => {
    if (!events.data) return

    setPages((current) => {
      const page: LoadedPage = { cursor: pageCursor, items: events.data?.items ?? EMPTY_EVENTS }
      const index = current.findIndex((item) => item.cursor === pageCursor)
      if (index < 0) return [...current, page]

      const next = [...current]
      next[index] = page
      return next
    })
  }, [events.data?.items, pageCursor])

  const storedItems = pages.flatMap((page) => page.items)
  const items = mergeClientEvents(storedItems, events.data?.items ?? EMPTY_EVENTS)
  const firstPage = pages.find((page) => page.cursor === null)?.items
  const visibleFirstPage = pageCursor === null ? events.data?.items ?? firstPage ?? EMPTY_EVENTS : firstPage ?? EMPTY_EVENTS

  useMarkEventsReadOnView(visibleFirstPage, pageCursor === null ? events.data?.unread ?? 0 : 0)

  const initialLoading = items.length === 0 && events.isPending
  const loadingMore = items.length > 0 && events.isPending
  const hasMore = (events.data?.items.length ?? 0) === PAGE_SIZE

  if (initialLoading) {
    return (
      <div className={styles.loading} aria-label="Загружаем события клиентов">
        <SkeletonStack rows={6} height={72} />
      </div>
    )
  }

  if (events.isError && items.length === 0) {
    return (
      <div className={styles.state} role="alert">
        <span className={styles.stateIcon} aria-hidden="true">
          <ErrorIcon />
        </span>
        <h2 className={styles.stateTitle}>Не удалось загрузить ленту</h2>
        <p className={styles.stateHint}>{errorMessage(events.error)}</p>
        <Button variant="secondary" size="sm" onClick={() => void events.refetch()}>
          Попробовать снова
        </Button>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className={styles.state}>
        <span className={styles.stateIcon} aria-hidden="true">
          <EmptyIcon />
        </span>
        <h2 className={styles.stateTitle}>
          {filtered ? 'По этим фильтрам событий нет' : 'Событий пока нет'}
        </h2>
        <p className={styles.stateHint}>
          {filtered
            ? 'Измените фильтры или сбросьте их, чтобы увидеть всю ленту.'
            : 'После первого разобранного разговора здесь появятся изменения карточки клиента.'}
        </p>
      </div>
    )
  }

  return (
    <div className={styles.feed} role="feed" aria-busy={events.isFetching || undefined}>
      <div className={styles.rows}>
        {items.map((event) => (
          <ClientEventRow key={event.id} event={event} />
        ))}
      </div>

      <div className={styles.footer}>
        {loadingMore ? (
          <Button variant="secondary" size="sm" loading>
            Показать ещё
          </Button>
        ) : events.isError ? (
          <div className={styles.moreError} role="alert">
            <span>Следующая часть ленты не загрузилась.</span>
            <button type="button" onClick={() => void events.refetch()}>
              Повторить
            </button>
          </div>
        ) : hasMore ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const last = events.data?.items.at(-1)
              if (last) setCursor(last.id)
            }}
          >
            Показать ещё
          </Button>
        ) : (
          <span className={styles.complete}>Показаны все события</span>
        )}
      </div>
    </div>
  )
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Сервер не ответил'
}

function StarIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path d="M7.5 1.8L9.1 5L12.7 5.5L10.1 8L10.7 11.6L7.5 9.9L4.3 11.6L4.9 8L2.3 5.5L5.9 5L7.5 1.8Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  )
}

function EmptyIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M5 16H10L12.8 9.5L17 22.5L20 14.5H27" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="1.4" opacity="0.5" />
    </svg>
  )
}

function ErrorIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="1.5" />
      <path d="M16 9.5V17.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="16" cy="22" r="1.2" fill="currentColor" />
    </svg>
  )
}
